/* eslint-disable */
import PropTypes from 'prop-types';
import { makeStyles, Typography } from '@material-ui/core';
import { CheckCircle, AccountCircle, AssistantPhoto } from '@material-ui/icons';
import USER_ROLES from 'components/Users/constants';

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
    },
  },
  roleCard: {
    flex: 1,
    height: 140,
    minWidth: 200,
    position: 'relative',
    padding: theme.spacing(3),
    border: '2px solid',
    borderColor: 'rgba(0, 0, 0, 0.12)',
    borderRadius: 12,
    cursor: 'pointer',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    background: 'linear-gradient(135deg, #ffffff 0%, #fafafa 100%)',
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    '&:hover': {
      borderColor: theme.palette.primary.light,
      transform: 'translateY(-2px)',
      boxShadow: '0 8px 24px rgba(0, 168, 255, 0.15)',
      '& $roleIcon': {
        transform: 'scale(1.1)',
      },
    },
    '&:active': {
      transform: 'translateY(0)',
    },
  },
  roleCardSelected: {
    borderColor: theme.palette.primary.main,
    background: 'linear-gradient(135deg, #ffffff 0%, #f0f9ff 100%)',
    boxShadow: '0 4px 16px rgba(0, 168, 255, 0.2)',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: 4,
      background: theme.palette.primary.main,
    },
  },
  roleHeader: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
  },
  roleIcon: {
    fontSize: 32,
    marginRight: theme.spacing(1.5),
    transition: 'transform 0.3s ease',
  },
  providerIcon: {
    color: theme.colors?.blue || '#2E48D3',
  },
  assistantIcon: {
    color: theme.colors?.lightBlue2 || '#6C7FDF',
  },
  roleTextContainer: {
    flex: 1,
  },
  roleTitle: {
    fontSize: '1.1rem',
    fontWeight: 600,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(0.5),
  },
  roleDescription: {
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
    lineHeight: 1.4,
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
  checkIcon: {
    position: 'absolute',
    top: theme.spacing(2),
    right: theme.spacing(2),
    color: theme.palette.primary.main,
    opacity: 0,
    transform: 'scale(0.8)',
    transition: 'all 0.3s ease',
  },
  checkIconVisible: {
    opacity: 1,
    transform: 'scale(1)',
  },
}));

const RoleSelector = ({ value, onChange, forms }) => {
  const classes = useStyles();

  const roles = [
    {
      value: USER_ROLES.PROVIDER,
      title: forms.provider || 'Provider',
      description: 'Medical professional who can manage patient care and view reports',
      icon: <AccountCircle className={`${classes.roleIcon} ${classes.providerIcon}`} />,
    },
    {
      value: USER_ROLES.ASSISTANT,
      title: forms.assistant || 'Assistant',
      description: 'Support staff who can help with administrative tasks',
      icon: <AssistantPhoto className={`${classes.roleIcon} ${classes.assistantIcon}`} />,
    },
  ];

  return (
    <div className={classes.container}>
      {roles.map((role) => {
        const isSelected = value === role.value;
        return (
          <div
            key={role.value}
            className={`${classes.roleCard} ${isSelected ? classes.roleCardSelected : ''}`}
            onClick={() => onChange(role.value)}
            role="button"
            tabIndex={0}
            onKeyPress={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                onChange(role.value);
              }
            }}
          >
            <CheckCircle
              className={`${classes.checkIcon} ${isSelected ? classes.checkIconVisible : ''}`}
            />
            <div className={classes.roleHeader}>
              {role.icon}
              <div className={classes.roleTextContainer}>
                <Typography className={classes.roleTitle}>
                  {role.title}
                </Typography>
                <Typography className={classes.roleDescription}>
                  {role.description}
                </Typography>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

RoleSelector.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  forms: PropTypes.object.isRequired,
};

export default RoleSelector;
